import React, { useEffect, useState, useRef } from 'react';
import API from '../api';
import Navbar from '../components/Navbar';
import toast from 'react-hot-toast';
import { priceToCode, getPriceCodeSettings } from '../utils/priceCode';

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [search, setSearch] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierDetail, setSupplierDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('bills');

  const [showNewBillForm, setShowNewBillForm] = useState(false);
const [billItems, setBillItems] = useState([{ product_name: '', quantity: '', buying_price: '', selling_price: '', low_stock_threshold: '', manufacturer_barcode: '' }]);
  const [scanning, setScanning] = useState(false);
  const [scanTarget, setScanTarget] = useState(null);
  const scannerRef = useRef(null);

  const startScanner = (formType, index) => {
    setScanTarget({ formType, index });
    setScanning(true);
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
    }
    setScanning(false);
  };

  useEffect(() => {
    if (scanning) {
      import('html5-qrcode').then(({ Html5Qrcode }) => {
        const scanner = new Html5Qrcode('supplier-qr-reader');
        scannerRef.current = scanner;
        scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            try {
              stopScanner();
              const { formType, index } = scanTarget;
              const setItemsFn = formType === 'new' ? setBillItems : setAddMoreBillItems;
              const currentItems = formType === 'new' ? billItems : addMoreBillItems;

              API.get(`/products/lookup/${decodedText}`)
                .then((res) => {
                  const product = res.data;
                  const updated = [...currentItems];
                  updated[index] = {
                    ...updated[index],
                    product_name: product.name,
                    buying_price: product.buying_price,
                    selling_price: product.selling_price,
                    manufacturer_barcode: decodedText,
                  };
                  setItemsFn(updated);
                  toast.success(`${product.name} found — details auto-filled!`);
                })
                .catch((err) => {
                  if (err.response && err.response.status === 404) {
                    const updated = [...currentItems];
                    updated[index] = { ...updated[index], manufacturer_barcode: decodedText };
                    setItemsFn(updated);
                    toast.success('New product — barcode saved, please type name and prices');
                  } else {
                    toast.error('Error looking up barcode: ' + (err.message || 'unknown error'));
                  }
                });
            } catch (err) {
              toast.error('Scan handling error: ' + (err.message || 'unknown error'));
            }
          },
          () => {}
        ).catch((err) => {
          toast.error('Camera error: ' + (err.message || 'unknown error'));
          setScanning(false);
        });
      });
    }
    return () => {
      if (scannerRef.current) scannerRef.current.stop().catch(() => {});
    };
  }, [scanning]);  
  const [supplierBills, setSupplierBills] = useState([]);
  const [billPayAmount, setBillPayAmount] = useState({});
  const [billSearchQuery, setBillSearchQuery] = useState('');
const [expandedBillId, setExpandedBillId] = useState(null);
  const [billSearchResults, setBillSearchResults] = useState([]);
  const [customBillNumber, setCustomBillNumber] = useState('');  
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [showSupplierProducts, setShowSupplierProducts] = useState(false);
  const [billLowStockThreshold, setBillLowStockThreshold] = useState('');
const [addMoreBillItems, setAddMoreBillItems] = useState([{ product_name: '', quantity: '', buying_price: '', selling_price: '', low_stock_threshold: '', manufacturer_barcode: '' }]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const res = await API.get('/suppliers');
      setSuppliers(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await API.post('/suppliers/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      toast.success(res.data.message);
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Import failed');
    }
    setImporting(false);
    e.target.value = '';
  };

  const downloadTemplate = () => {
    const template = `Name,Phone,Email,Address,Balance Owed\n# DELETE EXAMPLE ROWS AND ADD YOUR SUPPLIERS\nABC Wholesale (EXAMPLE - DELETE),0112345678,abc@gmail.com,Colombo,25000\nXYZ Trading (EXAMPLE - DELETE),0113456789,,Kandy,0`;
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wahi-pos-suppliers-template.csv';
    a.click();
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const handleAddSupplier = async (e) => {
    e.preventDefault();
    try {
      await API.post('/suppliers', { name, phone, email, address });
      toast.success('Supplier added!');
      setName('');
      setPhone('');
      setEmail('');
      setAddress('');
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add supplier');
    }
  };
  const handleDeleteBill = async (billId) => {
    if (!window.confirm('Delete this bill? This only works if balance is LKR 0.')) return;
    try {
      await API.delete(`/suppliers/bills/${billId}`);
      toast.success('Bill deleted!');
      setSelectedBillId(null);
      fetchSupplierBills(selectedSupplier.id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete bill');
    }
  };

  const handleViewSupplier = async (supplier, billIdToOpen = null) => {
    setSelectedSupplier(supplier);
    setActiveTab('bills');
    setShowNewBillForm(false);
    setExpandedBillId(null);
    setSelectedBillId(null);
    setShowSupplierProducts(false);
    try {
      const res = await API.get(`/suppliers/${supplier.id}`);
      setSupplierDetail(res.data);
      fetchSupplierBills(supplier.id, billIdToOpen);
    } catch (err) {
      toast.error('Failed to load supplier');
    }
  };

  const fetchSupplierBills = async (supplierId, billIdToOpen = null) => {
    try {
      const res = await API.get(`/suppliers/${supplierId}/bills`);
      setSupplierBills(res.data);
      if (billIdToOpen) setSelectedBillId(billIdToOpen);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSupplierProducts = async (supplierId) => {
    try {
      const res = await API.get(`/suppliers/${supplierId}/products`);
      setSupplierProducts(res.data);
    } catch (err) {
      toast.error('Failed to load stock status');
    }
  };

  const toggleSupplierProducts = () => {
    if (!showSupplierProducts) {
      fetchSupplierProducts(selectedSupplier.id);
    }
    setShowSupplierProducts(!showSupplierProducts);
  };

  const handleDeleteSupplier = async (supplier) => {
    if (!window.confirm(`Delete ${supplier.name}? This only works if balance is LKR 0.`)) return;
    try {
      await API.delete(`/suppliers/${supplier.id}`);
      toast.success('Supplier deleted!');
      setSelectedSupplier(null);
      setSupplierDetail(null);
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete supplier. Balance must be LKR 0.');
    }
  };

  const [billPaymentType, setBillPaymentType] = useState('credit');
    const [selectedBillId, setSelectedBillId] = useState(null);
      const [returnQty, setReturnQty] = useState({});
    const [creatingBill, setCreatingBill] = useState(false);
    const [addingBillItems, setAddingBillItems] = useState(false);


  const handleCreateBill = async () => {
    if (creatingBill) return;
    for (const item of billItems) {
      if (!item.product_name || !item.quantity || !item.buying_price || !item.selling_price) {
        toast.error('Please fill in all fields for each product');
        return;
      }
      if (parseFloat(item.selling_price) < parseFloat(item.buying_price)) {
        toast.error(`Selling price cannot be less than buying price for ${item.product_name}`);
        return;
      }
    }
    setCreatingBill(true);
    try {
const res = await API.post(`/suppliers/${selectedSupplier.id}/bills`, { items: billItems, bill_number: customBillNumber });
if (billPaymentType === 'paid_full') {
        const billTotal = billItems.reduce((sum, item) => sum + (parseFloat(item.buying_price || 0) * parseInt(item.quantity || 0)), 0);
        if (billTotal > 0) {
          await API.post(`/suppliers/bills/${res.data.id}/pay`, { amount: billTotal });
        }
      }
      toast.success(billPaymentType === 'paid_full' ? 'Purchase bill created and paid in full!' : 'Purchase bill created successfully!');
setBillItems([{ product_name: '', quantity: '', buying_price: '', selling_price: '', low_stock_threshold: '' }]);
      setBillPaymentType('credit');
      setCustomBillNumber('');
      setShowNewBillForm(false);
      fetchSupplierBills(selectedSupplier.id);
      handleViewSupplier(selectedSupplier);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create purchase bill');
    }
  };

  const handleAddItemsToBill = async (billId) => {
    for (const item of addMoreBillItems) {
      if (!item.product_name || !item.quantity || !item.buying_price || !item.selling_price) {
        toast.error('Please fill in all fields for each product');
        return;
      }
      if (parseFloat(item.selling_price) < parseFloat(item.buying_price)) {
        toast.error(`Selling price cannot be less than buying price for ${item.product_name}`);
        return;
      }
    }
      setAddingBillItems(true);

    try {
      await API.put(`/suppliers/bills/${billId}/add-items`, { items: addMoreBillItems });
      toast.success('Items added to bill!');
      setAddMoreBillItems([{ product_name: '', quantity: '', buying_price: '', selling_price: '', low_stock_threshold: '' }]);
      setExpandedBillId(null);
      fetchSupplierBills(selectedSupplier.id);
      handleViewSupplier(selectedSupplier);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add items to bill');
    }
    setAddingBillItems(false);
  };

  const handlePayBill = async (billId) => {
    const amount = billPayAmount[billId];
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    try {
      await API.post(`/suppliers/bills/${billId}/pay`, { amount: parseFloat(amount) });
      toast.success('Payment recorded successfully!');
      setBillPayAmount({ ...billPayAmount, [billId]: '' });
      fetchSupplierBills(selectedSupplier.id);
      handleViewSupplier(selectedSupplier);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record payment');
    }
  };

  const handleReturnItem = async (billId, itemId) => {
    const qty = returnQty[itemId];
    if (!qty || qty < 1) {
      toast.error('Please enter return quantity');
      return;
    }
    if (!window.confirm(`Return ${qty} item(s) to supplier?`)) return;
    try {
      await API.put(`/suppliers/bills/${billId}/return-item`, {
        item_id: itemId,
        return_quantity: parseInt(qty),
      });
      toast.success('Item returned to supplier!');
      setReturnQty({ ...returnQty, [itemId]: '' });
      fetchSupplierBills(selectedSupplier.id);
      handleViewSupplier(selectedSupplier);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to return item');
    }
  };

  const handleSearchBill = async () => {
    if (!billSearchQuery.trim()) {
      toast.error('Please enter a bill number or product name');
      return;
    }
    try {
      const res = await API.get(`/suppliers/bills/search?query=${encodeURIComponent(billSearchQuery.trim())}`);
      setBillSearchResults(res.data);
    } catch (err) {
      setBillSearchResults([]);
      toast.error(err.response?.data?.message || 'No bills found');
    }
  };

  const handleSelectSearchResult = (bill) => {
    const supplier = suppliers.find((s) => s.id === bill.supplier_id);
    if (supplier) {
      handleViewSupplier(supplier, bill.id);
    }
    setBillSearchResults([]);
    setBillSearchQuery('');
  };

  const filteredSuppliers = suppliers.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );

 return (
    <div>
      <Navbar />
      {scanning && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '400px', boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }}>
            <h3 style={{ marginBottom: '1rem' }}>📷 Scan Product Barcode</h3>
            <div id="supplier-qr-reader" style={{ width: '100%' }}></div>
            <button onClick={stopScanner} style={{ width: '100%', padding: '10px', marginTop: '1rem', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
              Cancel Scan
            </button>
          </div>
        </div>
      )}
      <div style={styles.container} className="container">
        <h2 style={styles.title}>Suppliers</h2>

        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '16px', margin: 0 }}>Add New Supplier</h3>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={downloadTemplate} style={styles.templateBtn}>📥 Download Template</button>
              <label style={styles.importBtn}>
                {importing ? '⏳ Importing...' : '📤 Import CSV'}
                <input type="file" accept=".xlsx,.xls,.csv" onChange={handleImport} style={{ display: 'none' }} disabled={importing} />
              </label>
            </div>
          </div>
          <form onSubmit={handleAddSupplier} style={styles.form} className="form">
            <input style={styles.input} type="text" placeholder="Supplier Name" value={name} onChange={(e) => setName(e.target.value)} required />
            <input style={styles.input} type="text" placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            <input style={styles.input} type="email" placeholder="Email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} />
            <input style={styles.input} type="text" placeholder="Address (optional)" value={address} onChange={(e) => setAddress(e.target.value)} />
            <button style={styles.button} type="submit">Add Supplier</button>
          </form>
          {importResult && (
            <div style={styles.importResult}>
              <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>✅ {importResult.imported} imported | ⚠️ {importResult.skipped} skipped</p>
              {importResult.errors?.length > 0 && (
                <div>{importResult.errors.map((e, i) => (<p key={i} style={{ fontSize: '12px', color: '#ef4444' }}>• {e}</p>))}</div>
              )}
            </div>
          )}
        </div>

        <input style={styles.search} type="text" placeholder="Search suppliers..." value={search} onChange={(e) => setSearch(e.target.value)} />

        <div style={styles.layout} className="layout">
          <div className="mobile-scroll-wrapper"><p className="mobile-scroll-hint">← Swipe to see more →</p><table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Phone</th>
              </tr>
            </thead>
            <tbody>
              {filteredSuppliers.length === 0 ? (
                <tr><td colSpan="2" style={{ ...styles.td, textAlign: 'center', color: '#888' }}>No suppliers found</td></tr>
              ) : (
                filteredSuppliers.map((s) => (
                  <tr key={s.id} onClick={() => handleViewSupplier(s)} style={{ ...styles.row, background: selectedSupplier?.id === s.id ? '#e0e7ff' : '#fff' }}>
                    <td style={styles.td}>{s.name}</td>
                    <td style={styles.td}>{s.phone || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table></div>

          {supplierDetail && (
            <div style={styles.detail}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h3 style={{ fontSize: '18px', margin: 0 }}>{supplierDetail.name}</h3>
              </div>
              <p style={styles.detailText}>{supplierDetail.phone}</p>
              {supplierDetail.email && <p style={styles.detailText}>{supplierDetail.email}</p>}
              {supplierDetail.address && <p style={styles.detailText}>{supplierDetail.address}</p>}

              <div style={styles.balanceBox}>
                <div style={styles.balanceRow}><span>Total Purchased</span><span>LKR {parseFloat(supplierDetail.total_purchased).toFixed(2)}</span></div>
                <div style={styles.balanceRow}><span style={{ color: '#f59e0b' }}>Returned to Supplier</span><span style={{ color: '#f59e0b' }}>- LKR {parseFloat(supplierDetail.total_returned).toFixed(2)}</span></div>
                <div style={styles.balanceRow}><span style={{ color: '#16a34a' }}>Total Paid</span><span style={{ color: '#16a34a' }}>- LKR {parseFloat(supplierDetail.total_paid).toFixed(2)}</span></div>
                <div style={{ ...styles.balanceRow, fontWeight: 'bold', fontSize: '15px', borderTop: '1px solid #ddd', paddingTop: '6px', marginTop: '4px' }}>
                  <span>Outstanding Balance</span>
                  <span style={{ color: supplierDetail.balance > 0 ? '#ef4444' : '#16a34a' }}>LKR {parseFloat(supplierDetail.balance).toFixed(2)}</span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input
                  type="text"
                  placeholder="Search bill number or product name..."
                  value={billSearchQuery}
                  onChange={(e) => setBillSearchQuery(e.target.value)}
                  style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }}
                />
                <button onClick={handleSearchBill} style={{ padding: '8px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>🔍 Find</button>
              </div>

              {billSearchResults.length > 0 && (
                <div style={{ background: '#f5f3ff', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem' }}>
                  <p style={{ fontSize: '12px', color: '#4f46e5', fontWeight: '600', marginBottom: '0.5rem' }}>
                    Found {billSearchResults.length} matching bill(s):
                  </p>
                  {billSearchResults.map((bill) => (
                    <div key={bill.id} onClick={() => handleSelectSearchResult(bill)}
                      style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: '#fff', borderRadius: '6px', marginBottom: '4px', cursor: 'pointer', fontSize: '13px' }}>
                      <span>Bill #{bill.bill_number} — {bill.supplier_name}</span>
                      <span style={{ color: '#888' }}>{new Date(bill.created_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4 style={{ fontSize: '14px', margin: 0 }}>Purchase Bills</h4>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={toggleSupplierProducts} style={{ ...styles.addProductBtn, background: '#16a34a' }}>
                    {showSupplierProducts ? 'Hide' : '📊'} Stock Status
                  </button>
                  <button onClick={() => { if (!showNewBillForm) fetchSupplierProducts(selectedSupplier.id); setShowNewBillForm(!showNewBillForm); }} style={styles.addProductBtn}>
                    {showNewBillForm ? 'Cancel' : '+ New Purchase Bill'}
                  </button>
                </div>
              </div>

              <datalist id="supplier-products-datalist">
                {supplierProducts.map((p) => (
                  <option key={p.id} value={p.name} />
                ))}
              </datalist>

              {showSupplierProducts && (
                <div style={{ background: '#f9f9ff', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
                  <p style={{ fontSize: '12px', color: '#888', marginBottom: '0.75rem' }}>
                    Based on sales over the last 30 days
                  </p>
                  {supplierProducts.length === 0 ? (
                    <p style={{ color: '#888', fontSize: '13px' }}>No products linked to this supplier yet</p>
                  ) : (
                    supplierProducts.map((p) => (
                      <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', background: '#fff', borderRadius: '6px', marginBottom: '4px', flexWrap: 'wrap', gap: '4px' }}>
                        <div>
                          <span style={{ fontSize: '13px', fontWeight: '600' }}>{p.name}</span>
                          <span style={{ fontSize: '12px', color: '#888', marginLeft: '8px' }}>Stock: {p.stock}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '11px', color: '#888' }}>
                            {p.sold_last_30_days > 0 ? `${p.sold_last_30_days} sold / 30 days` : 'No sales in 30 days'}
                          </span>
                          {p.status === 'low_stock' && (
                            <span style={{ fontSize: '11px', fontWeight: 'bold', padding: '3px 10px', borderRadius: '12px', background: '#fee2e2', color: '#ef4444' }}>🔴 Low Stock</span>
                          )}
                          {p.status === 'overstock' && (
                            <span style={{ fontSize: '11px', fontWeight: 'bold', padding: '3px 10px', borderRadius: '12px', background: '#fef3c7', color: '#d97706' }}>🟡 Overstock</span>
                          )}
                          {p.status === 'normal' && (
                            <span style={{ fontSize: '11px', fontWeight: 'bold', padding: '3px 10px', borderRadius: '12px', background: '#dcfce7', color: '#16a34a' }}>🟢 Normal</span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {showNewBillForm && (
                <div style={{ background: '#f9f9ff', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
                  {billItems.map((item, index) => (
                    <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      <input style={{ ...styles.modalInput, marginBottom: 0, flex: 2 }} type="text" placeholder="Product Name" value={item.product_name} list="supplier-products-datalist"
                        onChange={(e) => { const n = [...billItems]; n[index].product_name = e.target.value; setBillItems(n); }} />
                      <button type="button" onClick={() => startScanner('new', index)} style={{ padding: '8px 12px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                        📷 Scan
                      </button>
                      <input style={{ ...styles.modalInput, marginBottom: 0, flex: 1 }} type="number" placeholder="Qty" value={item.quantity}
                        onChange={(e) => { const n = [...billItems]; n[index].quantity = e.target.value; setBillItems(n); }} />
                      <input style={{ ...styles.modalInput, marginBottom: 0, flex: 1 }} type="number" placeholder="Buy Price" value={item.buying_price}
                        onChange={(e) => { const n = [...billItems]; n[index].buying_price = e.target.value; setBillItems(n); }} />
                      <input style={{ ...styles.modalInput, marginBottom: 0, flex: 1 }} type="number" placeholder="Sell Price" value={item.selling_price}
                        onChange={(e) => { const n = [...billItems]; n[index].selling_price = e.target.value; setBillItems(n); }} />
                      <input style={{ ...styles.modalInput, marginBottom: 0, flex: 1 }} type="number" placeholder="Low Stock Alert" value={item.low_stock_threshold}
                        onChange={(e) => { const n = [...billItems]; n[index].low_stock_threshold = e.target.value; setBillItems(n); }} />
                      {billItems.length > 1 && (
                        <button type="button" onClick={() => setBillItems(billItems.filter((_, i) => i !== index))} style={{ padding: '6px 10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>✕</button>
                      )}
                    </div>
                  ))}
                  <p style={{ fontSize: '11px', color: '#888', marginTop: '-0.25rem', marginBottom: '0.5rem' }}>
                    💡 Type a product name — if you've bought it from this supplier before, it'll suggest it so you don't accidentally create a duplicate.
                  </p>
                  <button type="button" onClick={() => setBillItems([...billItems, { product_name: '', quantity: '', buying_price: '', selling_price: '' }])}
                    style={{ padding: '6px 12px', background: '#e0e7ff', color: '#4f46e5', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', marginBottom: '0.75rem' }}>
                    + Add Another Product
                  </button>
                  <input
                    type="text"
                    placeholder="Supplier's Bill Number (optional — leave blank to auto-generate)"
                    value={customBillNumber}
                    onChange={(e) => setCustomBillNumber(e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', marginBottom: '0.75rem', boxSizing: 'border-box' }}
                  />
                  
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <button type="button" onClick={() => setBillPaymentType('credit')}
                      style={{ flex: 1, padding: '8px', borderRadius: '6px', border: billPaymentType === 'credit' ? '2px solid #4f46e5' : '1px solid #ddd', background: billPaymentType === 'credit' ? '#e0e7ff' : '#fff', color: billPaymentType === 'credit' ? '#4f46e5' : '#666', fontWeight: billPaymentType === 'credit' ? '600' : '400', cursor: 'pointer', fontSize: '13px' }}>
                      💳 Credit (pay later)
                    </button>
                    <button type="button" onClick={() => setBillPaymentType('paid_full')}
                      style={{ flex: 1, padding: '8px', borderRadius: '6px', border: billPaymentType === 'paid_full' ? '2px solid #16a34a' : '1px solid #ddd', background: billPaymentType === 'paid_full' ? '#dcfce7' : '#fff', color: billPaymentType === 'paid_full' ? '#16a34a' : '#666', fontWeight: billPaymentType === 'paid_full' ? '600' : '400', cursor: 'pointer', fontSize: '13px' }}>
                      💵 Paid in full now (cash)
                    </button>
                  </div>
                  <div style={styles.totalPreview}>
                    <span>Bill Total:</span>
                    <span style={{ fontWeight: 'bold', color: '#4f46e5' }}>
                      LKR {billItems.reduce((sum, item) => sum + (parseFloat(item.buying_price || 0) * parseInt(item.quantity || 0)), 0).toFixed(2)}
                    </span>
                  </div>
<button onClick={handleCreateBill} style={{ ...styles.addProductBtn, opacity: creatingBill ? 0.6 : 1 }} disabled={creatingBill}>
                    {creatingBill ? '💾 Saving...' : '💾 Save Purchase Bill'}
                  </button>                </div>
              )}

              {supplierBills.length === 0 ? (
                <p style={{ color: '#888', fontSize: '13px' }}>No purchase bills yet</p>
              ) : selectedBillId === null ? (
                <div>
                  {supplierBills.map((bill) => (
                    <div key={bill.id} onClick={() => setSelectedBillId(bill.id)}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', border: '1px solid #f0f0f0', borderRadius: '8px', padding: '0.75rem', marginBottom: '0.5rem', cursor: 'pointer' }}>
                      <div>
                        <span style={{ fontWeight: '600', fontSize: '13px' }}>Bill #{bill.bill_number}</span>
                        <span style={{ fontSize: '12px', color: '#888', marginLeft: '8px' }}>{new Date(bill.created_at).toLocaleDateString()}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '13px' }}>LKR {parseFloat(bill.net_total).toFixed(2)}</span>
                        <span style={{ fontSize: '12px', fontWeight: 'bold', padding: '3px 10px', borderRadius: '12px', background: bill.balance > 0 ? '#fee2e2' : '#dcfce7', color: bill.balance > 0 ? '#ef4444' : '#16a34a' }}>
                          {bill.balance > 0 ? `LKR ${parseFloat(bill.balance).toFixed(2)} owed` : 'Paid'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (() => {
                const bill = supplierBills.find((b) => b.id === selectedBillId);
                if (!bill) return null;
                return (
                  <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: '8px', padding: '0.75rem' }}>
                    <button onClick={() => setSelectedBillId(null)} style={{ padding: '4px 10px', background: '#e0e7ff', color: '#4f46e5', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', marginBottom: '0.75rem' }}>
                      ← Back to all bills
                    </button>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: '600', fontSize: '13px' }}>Bill #{bill.bill_number}</span>
                      <span style={{ fontSize: '12px', color: '#888' }}>{new Date(bill.created_at).toLocaleDateString()}</span>
                    </div>
                    {bill.items?.map((item) => {
                      const returnable = item.quantity - (item.returned_quantity || 0);
                      return (
                        <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap', gap: '4px' }}>
                          <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                            • {item.product_name} x{item.quantity}{item.returned_quantity > 0 ? ` (returned ${item.returned_quantity})` : ''} @ LKR {parseFloat(item.buying_price).toFixed(2)}
                          </p>
                          {returnable > 0 && (
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              <input type="number" min="1" max={returnable} placeholder="Qty" value={returnQty[item.id] || ''}
                                onChange={(e) => setReturnQty({ ...returnQty, [item.id]: e.target.value })}
                                style={{ width: '50px', padding: '3px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '11px' }} />
                              <button onClick={() => handleReturnItem(bill.id, item.id)} style={{ padding: '3px 8px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>↩️ Return</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px dashed #ddd', fontSize: '13px', flexWrap: 'wrap', gap: '4px' }}>
                      <span>Total: LKR {parseFloat(bill.net_total).toFixed(2)}</span>
                      <span style={{ color: '#16a34a' }}>Paid: LKR {parseFloat(bill.paid_amount).toFixed(2)}</span>
                      <span style={{ color: bill.balance > 0 ? '#ef4444' : '#16a34a', fontWeight: 'bold' }}>Balance: LKR {parseFloat(bill.balance).toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                      <button onClick={() => { if (expandedBillId !== bill.id) fetchSupplierProducts(selectedSupplier.id); setExpandedBillId(expandedBillId === bill.id ? null : bill.id); }} style={{ padding: '6px 12px', background: '#e0e7ff', color: '#4f46e5', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                        + Add Items
                      </button>
                      {bill.balance > 0 && (
                        <>
                          <input type="number" placeholder="Pay amount" value={billPayAmount[bill.id] || ''}
                            onChange={(e) => setBillPayAmount({ ...billPayAmount, [bill.id]: e.target.value })}
                            style={{ flex: 1, padding: '6px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '12px' }} />
                          <button onClick={() => handlePayBill(bill.id)} style={{ padding: '6px 12px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Pay</button>
                        </>
                      )}
                      {Math.abs(bill.balance) < 0.01 && (
                        <button onClick={() => handleDeleteBill(bill.id)} style={{ padding: '6px 12px', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                          🗑️ Delete Bill
                        </button>
                      )}
                    </div>

                    {expandedBillId === bill.id && (
                      <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px dashed #ddd' }}>
                        {addMoreBillItems.map((item, index) => (
                          <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                            <input style={{ flex: 2, padding: '6px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '12px' }} type="text" placeholder="Product Name" value={item.product_name} list="supplier-products-datalist"
                              onChange={(e) => { const n = [...addMoreBillItems]; n[index].product_name = e.target.value; setAddMoreBillItems(n); }} />
                            <button type="button" onClick={() => startScanner('addmore', index)} style={{ padding: '6px 10px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}>
                              📷 Scan
                            </button>
                            <input style={{ flex: 1, padding: '6px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '12px' }} type="number" placeholder="Qty" value={item.quantity}
                              onChange={(e) => { const n = [...addMoreBillItems]; n[index].quantity = e.target.value; setAddMoreBillItems(n); }} />
                            <input style={{ flex: 1, padding: '6px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '12px' }} type="number" placeholder="Buy Price" value={item.buying_price}
                              onChange={(e) => { const n = [...addMoreBillItems]; n[index].buying_price = e.target.value; setAddMoreBillItems(n); }} />
                           <input style={{ flex: 1, padding: '6px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '12px' }} type="number" placeholder="Sell Price" value={item.selling_price}
                              onChange={(e) => { const n = [...addMoreBillItems]; n[index].selling_price = e.target.value; setAddMoreBillItems(n); }} />
                            <input style={{ flex: 1, padding: '6px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '12px' }} type="number" placeholder="Low Stock Alert" value={item.low_stock_threshold || ''}
                              onChange={(e) => { const n = [...addMoreBillItems]; n[index].low_stock_threshold = e.target.value; setAddMoreBillItems(n); }} />
                          </div>
                        ))}
                        <button type="button" onClick={() => setAddMoreBillItems([...addMoreBillItems, { product_name: '', quantity: '', buying_price: '', selling_price: '', low_stock_threshold: '' }])}
                          style={{ padding: '4px 10px', background: '#e0e7ff', color: '#4f46e5', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', marginBottom: '0.5rem' }}>
                          + Add Row
                        </button>
                        <button onClick={() => handleAddItemsToBill(bill.id)} disabled={addingBillItems} style={{ width: '100%', padding: '8px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', opacity: addingBillItems ? 0.6 : 1 }}>
                          {addingBillItems ? '💾 Saving...' : '💾 Save Items to Bill'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {parseFloat(supplierDetail.balance) === 0 && (
                <button onClick={() => handleDeleteSupplier(selectedSupplier)} style={styles.deleteBtn}>🗑️ Delete Supplier</button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: { padding: '2rem' },
  title: { fontSize: '24px', marginBottom: '1.5rem' },
  card: { background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '1.5rem' },
  form: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  input: { padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', flex: 1, minWidth: '150px' },
  button: { padding: '10px 20px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  search: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', marginBottom: '1rem', boxSizing: 'border-box' },
  layout: { display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' },
  table: { flex: 1, minWidth: '250px', borderCollapse: 'collapse', background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  th: { padding: '12px 16px', background: '#4f46e5', color: '#fff', textAlign: 'left', fontSize: '14px' },
  td: { padding: '12px 16px', borderBottom: '1px solid #f0f0f0', fontSize: '14px', cursor: 'pointer' },
  row: { transition: 'background 0.2s' },
  detail: { flex: 2, minWidth: '320px', background: '#fff', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  detailText: { fontSize: '13px', color: '#888', margin: '2px 0' },
  balanceBox: { background: '#f5f3ff', borderRadius: '8px', padding: '0.75rem', margin: '1rem 0' },
  balanceRow: { display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '3px 0' },
  addProductBtn: { padding: '8px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  totalPreview: { display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', fontSize: '13px', marginBottom: '0.5rem' },
  modalInput: { width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', boxSizing: 'border-box' },
  deleteBtn: { width: '100%', padding: '10px', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', marginTop: '1rem' },
  templateBtn: { padding: '8px 14px', background: '#e0e7ff', color: '#4f46e5', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  importBtn: { padding: '8px 14px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  importResult: { background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '1rem', marginTop: '1rem' },
};

export default Suppliers;